/**
 * Talent points in a skill-build row.
 *
 * OpenDota's ability `dname` for `special_bonus_*` is often an unsubstituted
 * token (`+{s:bonus_damage}` or `+{s:bonus_radius} Unstable Concoction Radius`).
 * That string must never be painted inline — the slot is a talent-tree tile.
 * Hover uses the human remainder of the dname, a generic slug like "+200 Health",
 * or the short fallback "Talent". Unique slugs must not collapse to a hero name
 * (`special_bonus_unique_alchemist` is Alchemist the hero, not the talent).
 */
export function isTalentAbility(slug) {
    return typeof slug === 'string' && slug.startsWith('special_bonus_');
}
/** Raw Valve / OpenDota tokens that must not be shown as a skill icon label. */
export function looksLikeTalentToken(name) {
    return /\{s[:.]/i.test(name) || name.startsWith('special_bonus_') || /\{s/i.test(name);
}
const GENERIC_TALENTS = [
    { pattern: /^special_bonus_hp_(\d+)$/, label: (n) => `+${n} Health` },
    { pattern: /^special_bonus_mp_(\d+)$/, label: (n) => `+${n} Mana` },
    { pattern: /^special_bonus_hp_regen_(\d+)$/, label: (n) => `+${n} HP Regen` },
    { pattern: /^special_bonus_mp_regen_(\d+)$/, label: (n) => `+${n} Mana Regen` },
    { pattern: /^special_bonus_movement_speed_(\d+)$/, label: (n) => `+${n} Movement Speed` },
    { pattern: /^special_bonus_attack_damage_(\d+)$/, label: (n) => `+${n} Attack Damage` },
    { pattern: /^special_bonus_attack_speed_(\d+)$/, label: (n) => `+${n} Attack Speed` },
    { pattern: /^special_bonus_strength_(\d+)$/, label: (n) => `+${n} Strength` },
    { pattern: /^special_bonus_agility_(\d+)$/, label: (n) => `+${n} Agility` },
    { pattern: /^special_bonus_intelligence_(\d+)$/, label: (n) => `+${n} Intelligence` },
    { pattern: /^special_bonus_armor_(\d+)$/, label: (n) => `+${n} Armor` },
    { pattern: /^special_bonus_magic_resistance_(\d+)$/, label: (n) => `+${n}% Magic Resistance` },
    { pattern: /^special_bonus_lifesteal_(\d+)$/, label: (n) => `+${n}% Lifesteal` },
    { pattern: /^special_bonus_spell_lifesteal_(\d+)$/, label: (n) => `+${n}% Spell Lifesteal` },
    { pattern: /^special_bonus_cooldown_reduction_(\d+)$/, label: (n) => `+${n}% Cooldown Reduction` },
    { pattern: /^special_bonus_evasion_(\d+)$/, label: (n) => `+${n}% Evasion` },
    { pattern: /^special_bonus_exp_boost_(\d+)$/, label: (n) => `+${n}% Experience` },
    { pattern: /^special_bonus_gold_(\d+)$/, label: (n) => `+${n} Gold` },
];
export const TALENT_FALLBACK = 'Talent';
/**
 * Strip unsubstituted `{s:…}` placeholders from an OpenDota talent dname.
 *
 * `+{s:bonus_radius} Unstable Concoction Radius` → `Unstable Concoction Radius`.
 * A dname that is only a token becomes null so the caller can fall back.
 */
export function cleanTalentTokens(name) {
    if (name.length === 0 || name.startsWith('special_bonus_'))
        return null;
    let cleaned = name.replace(/\{s:[^}]+\}s?/gi, '');
    cleaned = cleaned.replace(/\{[^}]*\}/g, '');
    cleaned = cleaned.replace(/\s+/g, ' ').trim();
    cleaned = cleaned.replace(/\s+[+-]\s+/g, ' ');
    cleaned = cleaned.replace(/^[+-]\s+(?!\d)/, '');
    cleaned = cleaned.replace(/^[+-]$/, '');
    cleaned = cleaned.replace(/\s+/g, ' ').trim();
    if (cleaned.length < 2)
        return null;
    if (looksLikeTalentToken(cleaned))
        return null;
    if (/^ability \d+$/i.test(cleaned))
        return null;
    return cleaned;
}
export function formatTalentSlug(slug) {
    if (slug === 'special_bonus_attributes')
        return '+2 All Attributes';
    for (const { pattern, label } of GENERIC_TALENTS) {
        const match = pattern.exec(slug);
        if (match?.[1])
            return label(match[1]);
    }
    // Unique slugs are `special_bonus_unique_<hero>` or `…_<hero>_<n>`. Title-casing
    // that remainder prints the hero ("Alchemist"), which is the hover bug on
    // 8957890399. Those need the dname, not the slug.
    return TALENT_FALLBACK;
}
/**
 * Hover / API display name for a talent slot.
 *
 * Prefers a human OpenDota `dname`, then the human remainder after stripping
 * `{s:…}` tokens, then a generic slug (`+200 Health`). Never a raw token,
 * never a hero name derived from `unique_<hero>`.
 */
export function talentHoverLabel(slug, rawName) {
    if (rawName.length > 0 && !looksLikeTalentToken(rawName)) {
        const trimmed = rawName.trim();
        if (trimmed.length > 0)
            return trimmed;
    }
    const cleaned = cleanTalentTokens(rawName);
    if (cleaned)
        return cleaned;
    if (slug && slug.length > 0)
        return formatTalentSlug(slug);
    return TALENT_FALLBACK;
}
//# sourceMappingURL=talents.js.map